import modelDataRef from '../../assets/model.json';
import teaserDataRef from '../../assets/teasers.json';
import _ from 'lodash';

class ModelService {

    constructor() {
        this.modelData = modelDataRef;
        this.teaserData = teaserDataRef;
        this.prioritizeCategories();
        this.cleanModel();
    }

    async cleanModel() {
        this.getAllComponents().forEach(comp => {
            const validCapabilities = comp.categories.map(catId => this.getCategoryById(catId))
                .filter(cat => cat.capabilities !== undefined)
                .reduce((acc, category) => {
                    category.capabilities.forEach(cap => {
                        if (!acc.includes(cap)) {
                            acc = [...acc, cap];
                        }
                    });
                    return acc;
                }, []);
            const cleanedCapabilities = validCapabilities
                .filter(c => comp.capabilities !== undefined && comp.capabilities[c] !== undefined)
                .reduce((obj, c) => {
                    obj[c] = comp.capabilities[c];
                    return obj;
                }, {});

            comp.capabilities = cleanedCapabilities;

            this.resolveTransitiveConnections(comp);
        });
    }

    prioritizeCategories() {
        let priorities = {
            "agent": 10,
            "instrumentation-lib": 10,
            "format": 12,
            "transport": 15,
            "collector": 20,
            "storage": 30,
            "data-processing": 40,
            "visualization": 100,
            "dashboarding": 100,
            "alerting": 100
        }

        _.forEach(this.modelData.categories, c => {
            c.priority = priorities[c.id];
        });
    }

    getCategories() {
        return this.modelData['categories'];
    }

    getCategoryById(id) {
        return this.modelData['categories'].find(c => c.id === id);
    }

    getComponents(category) {
        return this.modelData.components.filter(c => c.categories.includes(category));
    }

    getSortedComponents(category) {
        var components = this.getComponents(category);
        return _.sortBy(components, 'name');
    }

    getComponentById(id) {
        var lookupId = id;

        // workaround due to id change
        if (id === 'influx-db') {
            lookupId = 'influxdata-influxdb';
        } else if (id === 'influx-kapacitor') {
            lookupId = 'influxdata-kapacitor';
        } else if (id === 'influx-telegraf') {
            lookupId = 'influxdata-telegraf';
        } else if (id === 'influx-chronograf') {
            lookupId = 'influxdata-chronograf';
        }

        return this.getAllComponents().find(c => c.id === lookupId);
    }

    getAllComponents() {
        return this.modelData['components'];
    }

    getCompatibleComponents(id) {
        return _.uniqBy([...this.getDownstreamComponents(id), ...this.getUpstreamComponents(id)],'id').filter(c => c.id !== id);
    }

    getDownstreamComponents(id){
        const targetComponent = this.getComponentById(id);
        var outbound = [];
        if (targetComponent.connections !== undefined && targetComponent.connections.dataTo !== undefined) {
            outbound = targetComponent.connections.dataTo.map(c => this.getComponentById(c)).filter(e => typeof e !== 'undefined');
        }

        var othersInbound = this.getAllComponents().filter(c => {
            return c.connections.dataFrom.includes(targetComponent.id) 
                || c.connections.dataFrom.some(e => targetComponent.connections.includesTo.includes(e))
                || c.connections.includesFrom.some(e => targetComponent.connections.dataTo.includes(e))
                || c.connections.includesFrom.some(e => targetComponent.connections.includesTo.includes(e));
        });
        
        return _.union(othersInbound,outbound).filter(c => c.id !== id);
    }

    getUpstreamComponents(id){
        const targetComponent = this.getComponentById(id);
        var inbound = [];
        if (targetComponent.connections !== undefined && targetComponent.connections.dataFrom !== undefined) {
            inbound = targetComponent.connections.dataFrom.map(c => this.getComponentById(c)).filter(e => typeof e !== 'undefined');
        }

        var othersOutbound = this.getAllComponents().filter(c => {
            return c.connections.dataTo.includes(targetComponent.id) 
                || c.connections.dataTo.some(e => targetComponent.connections.includesFrom.includes(e))
                || c.connections.includesTo.some(e => targetComponent.connections.dataFrom.includes(e))
                || c.connections.includesTo.some(e => targetComponent.connections.includesFrom.includes(e));
        });

        return _.union(othersOutbound,inbound).filter(c => c.id !== id);
    }

    getFormatsOnEdges(fromCompId, toCompId){
        const fromComp = this.getComponentById(fromCompId);
        const toComp = this.getComponentById(toCompId);
        if(fromComp.connections.dataTo.includes(toComp) || toComp.connections.dataFrom.includes(fromComp)) {
            return undefined;
        }

        var fromIncludes = fromComp.connections.dataTo.filter(to => toComp.connections.includesFrom.includes(to));
        var toIncludes = toComp.connections.dataFrom.filter(from => fromComp.connections.includesTo.includes(from));
        var includesToIncludes = fromComp.connections.includesTo.filter(to => toComp.connections.includesFrom.includes(to));
        var includesFromIncludes = toComp.connections.includesFrom.filter(from => fromComp.connections.includesTo.includes(from));

        var resultList = [...fromIncludes,...toIncludes,...includesToIncludes,...includesFromIncludes];
        if(resultList.length <= 0){
            return undefined;
        }

        return resultList.filter(f => this.getComponentById(f).categories.includes("format"));
    }

    getLicenseLink(licenseName) {
        return this.modelData['licenses'][licenseName];
    }

    getGithubLink(id) {
        return this.getComponentById(id).github;
    }

    getComponentCapabilities(component) {
        const validCapabilities = component.categories.reduce((acc, catId) => {
            acc = [...acc, ...this.getCategoryById(catId).capabilities];
            return acc;
        }, []);
        Object.keys(component.capabilities).filter(cap => validCapabilities.find(cap))
    }

    getFilters() {
        const components = this.getAllComponents();
        const filters = components.reduce((acc, comp) => {
            const capabilities = comp['capabilities'];
            if (capabilities !== undefined) {
                const keys = Object.keys(capabilities);
                keys.forEach(key => {
                    const capValues = capabilities[key];
                    if (Array.isArray(capValues)) {
                        const relevantCategories = this.getCategories().filter(c => c.capabilities !== undefined && c.capabilities.includes(key)).map(c => c.id);
                        if (relevantCategories !== undefined && relevantCategories.length > 0) {
                            capValues.map(v => ({ capability: key, value: v, categories: relevantCategories })).forEach(item => {
                                const existingItem = acc.find(_i => this.equalFilterItems(_i, item));
                                if (existingItem === undefined) {
                                    acc = [...acc, item];
                                }
                            });
                        }
                    }
                });
            }

            return acc;
        }, []);

        const groupedItems = filters.reduce((acc, item) => {
            if (acc[item.capability] === undefined) {
                acc[item.capability] = [];
            }
            acc[item.capability].push(item);
            return acc;
        }, {});

        const items = Object.keys(groupedItems).sort().reduce((acc, key) => {
            const itemArray = groupedItems[key].sort((a, b) => {
                if (a.value < b.value) return -1;
                if (a.value > b.value) return 1;
                return 0;
            }).filter(item => item != null);
            return acc.concat(itemArray);
        }, []);
        return items;
    }

    getFilter(capability, value) {
        return filters.find(f => this.equalFilterItems(f, { capability: capability, value: value }));
    }


    equalFilterItems(itemA, itemB) {
        return itemA.capability === itemB.capability && itemA.value === itemB.value;
    }

    getAllTeasers() {
        return this.teaserData;
    }

    resolveTransitiveConnections(componentObj) {
        if(componentObj.connections === undefined){
            componentObj.connections = {};
        } 
        componentObj.connections.dataTo = _.uniq(this.resolveTransitiveIncludesConnections(componentObj.id, 'To')).filter(e => e !== componentObj.id);
        componentObj.connections.dataFrom = _.uniq(this.resolveTransitiveIncludesConnections(componentObj.id, 'From')).filter(e => e !== componentObj.id);
        componentObj.connections.includesTo = _.uniq(this.resolveTransitiveIncludes(componentObj.id, 'To')).filter(e => e !== componentObj.id);
        componentObj.connections.includesFrom = _.uniq(this.resolveTransitiveIncludes(componentObj.id, 'From')).filter(e => e !== componentObj.id);
    }

    resolveTransitiveIncludesConnections(componentId, toOrFrom) {
        let component = this.getComponentById(componentId);
        if(component.connections !== undefined) {
            let selfConnections = [];
            if(component.connections['data'+toOrFrom] !== undefined) {
                selfConnections = component.connections['data'+toOrFrom];
                if(component.connections['includes'+toOrFrom]!== undefined && component.connections['includes'+toOrFrom].length > 0){
                    selfConnections = component.connections['data'+toOrFrom].filter(con => !component.connections['includes'+toOrFrom].includes(con));
                }            
            }
            let additionalConnections = [];
            if(component.connections['includes'+toOrFrom] !== undefined && component.connections['includes'+toOrFrom].length > 0){
                additionalConnections = component.connections['includes'+toOrFrom].reduce((resultList,inc) => {
                    resultList = [...resultList, ...this.resolveTransitiveIncludesConnections(inc, toOrFrom)];
                    return resultList;
                }, []);
            }
            return [...selfConnections, ...additionalConnections];
        }
        return [];
    }

    resolveTransitiveIncludes(componentId, toOrFrom){
        let component = this.getComponentById(componentId);
        if(component.connections !== undefined && component.connections['includes'+toOrFrom] !== undefined) {
            let additionalIncludes = component.connections['includes'+toOrFrom].reduce((resultList,inc) => {
                return [...resultList, ...this.resolveTransitiveIncludes(inc, toOrFrom)];
            }, []);
            
            return [...component.connections['includes'+toOrFrom], ...additionalIncludes];
        }
        return [];
    }
}

export const modelService = new ModelService();
export const filters = modelService.getFilters();


// WEBPACK FOOTER //
// ./src/services/model/index.js